// Ported from the native app's JournalData.swift. Images are stored as PNG
// data URLs in chrome.storage.local under per-trade keys (the extension
// equivalent of the native app's Application Support/JournalImages PNGs),
// keeping the main journal entry list small and fast to load.

import { Emitter, onStorageChange, storageGet, storageSet, storageRemove } from "./storage.js";

export type TradeDirection = "long" | "short";

/** A management action taken during the trade (moved stop, took partial, added),
 * timestamped so it can be redrawn on the chart at the bar it happened. */
export interface ManageEvent {
  time: number;   // epoch ms
  price: number;
  kind: "stop" | "target" | "partial" | "add" | "note";
  note?: string;
}

/** A slice of real candles captured at log time so a trade can always be
 * reconstructed on the chart, even after the data feed's history window rolls
 * past it (Yahoo only serves ~60d of intraday). Compact by design — far
 * smaller than a screenshot. */
export interface CandleSnapshot {
  interval: string; // the timeframe the trade was taken on (e.g. "30m")
  bars: Array<{ t: number; o: number; h: number; l: number; c: number }>;
}

export interface TradeEntry {
  id: string;
  date: number; // epoch ms — the entry datetime
  symbol: string;
  direction: TradeDirection;
  rMultiple: number;
  setupTag: string;
  thesis: string;
  whatHappened: string;
  lesson: string;
  hasImage?: boolean;
  /** Whose trade this is: "Me" (default) or another trader's name. Own
   * analytics filter to "Me" so others' trades never skew personal stats. */
  trader?: string;
  /** Chart timeframe the trade was captured from (e.g. "30m"), enabling
   * "open the chart I actually traded" from the review. */
  timeframe?: string;
  /** Optional fill prices. Not needed for R-first journaling — they feed
   * future exit-counterfactual analysis (Product_Roadmap), which needs
   * real fills to replay a trade against chart history. */
  entryPrice?: number;
  exitPrice?: number;
  // --- Extended trade-craft + ledger fields (parity with a full prop journal;
  // all optional so the R-first core stays lean). Money fields are opt-in. ---
  session?: string; // Asia / London / New York
  dailyBias?: string; // Bullish / Bearish / Neutral
  timeHeldMinutes?: number;
  targetPrice?: number; // planned target
  stopPrice?: number; // planned stop / SL
  entryType?: string; // market / limit / stop
  size?: number; // position size (contracts/lots/units)
  confluence1?: string;
  confluence2?: string;
  ticks?: number; // result in ticks
  maxTicks?: number; // MFE in ticks
  riskPct?: number; // risk taken (% of account)
  fees?: number;
  netPnl?: number; // net P/L in account currency
  account?: string;
  tvLink?: string; // external TradingView chart link
  /** Optional link to a saved strategy (composable builder) this trade came
   * from — ties the journal to strategy development (manual or, later,
   * auto-logged from a deployment). */
  strategy?: string;
  /** How the trade was taken: manual (default), paper, or live. Lets the
   * journal separate practice from real money without extra fields. */
  mode?: "manual" | "paper" | "live";
  /** Set for auto-logged trades — the deployment/broker fill id. Used to
   * de-duplicate so re-reporting the same closed trade never doubles it. */
  externalId?: string;
  // --- In-app trade reconstruction (D-NNN): a trade logged from the chart
  // carries the times of its entry/exit and its management events, plus a
  // snapshot of the candles around it, so "View on chart" can always redraw it
  // (entry/stop/target/exit + hold window) without a stored screenshot. ---
  entryTime?: number;  // epoch ms of the entry bar (date is kept as the primary sort key)
  exitTime?: number;   // epoch ms of the exit bar
  manage?: ManageEvent[];
  snapshot?: CandleSnapshot;
}

const ENTRIES_KEY = "journalEntries";
const TAGS_KEY = "journalCustomTags";
const TRADERS_KEY = "journalTraders";
// Legacy per-trade image blobs (chart-image journaling, removed for launch).
// Kept only so deleting an old entry can still clean up its stored image.
const imageKey = (tradeID: string) => `journalImage-${tradeID}`;

export const defaultTags = ["Breakout", "Reversal", "Trend", "Scalp", "News"];

/** Minimal RFC-4180-ish CSV splitter: handles quoted fields, escaped quotes,
 * and commas/newlines inside quotes. Returns rows of string cells. */
function csvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  const s = text.replace(/\r\n?/g, "\n");
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') { if (s[i + 1] === '"') { cell += '"'; i++; } else inQuotes = false; }
      else cell += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { row.push(cell); cell = ""; }
    else if (c === "\n") { row.push(cell); rows.push(row); row = []; cell = ""; }
    else cell += c;
  }
  if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

/** The user's own trades. Own analytics filter to this so other traders'
 * imported trades never skew personal stats. */
export const DEFAULT_TRADER = "Me";

/** Migrate legacy entries (no `trader`) to the default owner. */
function withTraders(entries: TradeEntry[]): TradeEntry[] {
  return entries.map((e) => (e.trader ? e : { ...e, trader: DEFAULT_TRADER }));
}

// --- DEV: seed a few demo paper trades so the Review UI isn't empty before the
// deploy→report pipeline is wired. Set SEED_DEMO_TRADES = false (or clear the
// journalDemoSeededV1 storage key) to stop/reset. Seeds once, only into an
// empty journal, so it never clobbers real trades. Remove before ship.
const SEED_DEMO_TRADES = true;
const DEMO_SEEDED_KEY = "journalDemoSeededV2";
function demoTrades(): TradeEntry[] {
  const day = 86_400_000;
  const now = Date.now();
  // symbol, dir, R, entry, exit, size, held(min), setup — a plausible gold run.
  const rows: Array<[string, TradeDirection, number, number, number, number, number, string]> = [
    ["GC=F", "long", 2.1, 2402.4, 2417.7, 2, 95, "Pullback"],
    ["GC=F", "long", -1.0, 2431.0, 2423.8, 2, 40, "Pullback"],
    ["GC=F", "short", 1.6, 2456.2, 2444.9, 1, 130, "Fade"],
    ["GC=F", "long", 3.2, 2418.0, 2441.5, 2, 210, "Breakout"],
    ["GC=F", "short", -1.0, 2470.5, 2477.9, 1, 55, "Fade"],
    ["GC=F", "long", 0.8, 2409.1, 2414.6, 2, 70, "Pullback"],
  ];
  const barMs = 30 * 60_000; // 30m demo timeframe
  // A synthetic candle window around a trade: gentle noise, drifting from entry
  // to exit across the hold, with ~10 bars of context each side.
  const synthBars = (entry: number, exit: number, entryTime: number, exitTime: number) => {
    const held = Math.max(1, Math.round((exitTime - entryTime) / barMs));
    const lead = 10, tail = 10;
    const t0 = entryTime - lead * barMs;
    const total = lead + held + tail;
    const bars: Array<{ t: number; o: number; h: number; l: number; c: number }> = [];
    let prevClose = entry - (exit - entry) * 0.3;
    for (let k = 0; k < total; k++) {
      const t = t0 + k * barMs;
      // Target price along the path: flat-ish before entry, drift to exit during
      // the hold, flat-ish after.
      const prog = Math.min(1, Math.max(0, (k - lead) / held));
      const base = k < lead ? entry : entry + (exit - entry) * prog;
      const noise = (Math.sin(k * 1.7) + (Math.random() - 0.5)) * Math.abs(exit - entry) * 0.18;
      const o = prevClose;
      const c = base + noise;
      const h = Math.max(o, c) + Math.random() * Math.abs(exit - entry) * 0.12;
      const l = Math.min(o, c) - Math.random() * Math.abs(exit - entry) * 0.12;
      bars.push({ t, o: +o.toFixed(2), h: +h.toFixed(2), l: +l.toFixed(2), c: +c.toFixed(2) });
      prevClose = c;
    }
    return bars;
  };
  return rows.map(([symbol, direction, r, entry, exit, size, held, setup], i) => {
    const date = now - (rows.length - i) * day - Math.floor(Math.random() * day * 0.5);
    const exitTime = date + held * 60_000;
    const stop = direction === "long" ? entry - Math.abs(exit - entry) / Math.max(0.5, r) : entry + Math.abs(exit - entry) / Math.max(0.5, r);
    const target = direction === "long" ? entry + Math.abs(exit - entry) : entry - Math.abs(exit - entry);
    return {
      id: crypto.randomUUID(),
      externalId: `demo-${i}`,
      date,
      entryTime: date,
      exitTime,
      symbol,
      direction,
      rMultiple: r,
      setupTag: setup,
      thesis: "", whatHappened: "", lesson: "",
      timeframe: "30m",
      entryPrice: entry,
      exitPrice: exit,
      stopPrice: +stop.toFixed(2),
      targetPrice: +target.toFixed(2),
      size,
      timeHeldMinutes: held,
      netPnl: Math.round((exit - entry) * (direction === "short" ? -1 : 1) * size * 100) / 100,
      strategy: "Gold Pullback (demo)",
      mode: "paper",
      trader: DEFAULT_TRADER,
      snapshot: { interval: "30m", bars: synthBars(entry, exit, date, exitTime) },
    };
  });
}

export class JournalStore {
  entries: TradeEntry[] = [];
  customTags: string[] = [];
  /** Trader names the user added (persist even with zero trades). */
  customTraders: string[] = [];
  readonly changed = new Emitter();
  private initialized = false;

  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
    this.entries = withTraders(await storageGet<TradeEntry[]>(ENTRIES_KEY, []));
    this.customTags = await storageGet<string[]>(TAGS_KEY, []);
    this.customTraders = await storageGet<string[]>(TRADERS_KEY, []);
    // Reseed the demo when this version hasn't run — but only into an empty
    // journal or one that holds nothing but earlier demo trades, so real trades
    // are never touched.
    const onlyDemo = this.entries.every((e) => e.externalId?.startsWith("demo-"));
    if (SEED_DEMO_TRADES && onlyDemo && !(await storageGet<boolean>(DEMO_SEEDED_KEY, false))) {
      this.entries = demoTrades();
      await this.persistEntries();
      await storageSet(DEMO_SEEDED_KEY, true);
    }
    onStorageChange<TradeEntry[]>(ENTRIES_KEY, (v) => {
      this.entries = withTraders(v ?? []);
      this.changed.emit();
    });
    this.changed.emit();
  }

  private async persistEntries(): Promise<void> {
    await storageSet(ENTRIES_KEY, this.entries);
  }

  private async deleteImageIfPresent(entry: TradeEntry): Promise<void> {
    if (!entry.hasImage) return;
    await storageRemove(imageKey(entry.id));
  }

  async addTrade(entry: TradeEntry): Promise<void> {
    this.entries.unshift(entry);
    await this.persistEntries();
    this.changed.emit();
  }

  /** Auto-log a trade reported by a paper/live deployment (the receiving end of
   * the build→deploy→review loop). Idempotent on `externalId` so re-reporting
   * the same closed trade never duplicates it. Callable from any context
   * (e.g. the background worker polling the trading server); popups pick it up
   * via the ENTRIES_KEY storage listener. Returns false if it was a duplicate.
   *
   * The EMITTER — the background poller of the trading server's closed trades —
   * is the next backend milestone (services/api); this is the journal side,
   * ready to receive. */
  async logAutoTrade(t: {
    externalId?: string;
    date?: number;
    symbol: string;
    direction: TradeDirection;
    rMultiple?: number;
    strategy?: string;
    mode: "paper" | "live";
    entryPrice?: number;
    exitPrice?: number;
    netPnl?: number;
    size?: number;
    setupTag?: string;
    timeframe?: string;
  }): Promise<boolean> {
    if (t.externalId && this.entries.some((e) => e.externalId === t.externalId)) return false;
    await this.addTrade({
      id: crypto.randomUUID(),
      externalId: t.externalId,
      date: t.date ?? Date.now(),
      symbol: t.symbol.toUpperCase(),
      direction: t.direction,
      rMultiple: t.rMultiple ?? 0,
      setupTag: t.setupTag ?? t.strategy ?? "auto",
      thesis: "", whatHappened: "", lesson: "",
      strategy: t.strategy,
      mode: t.mode,
      entryPrice: t.entryPrice,
      exitPrice: t.exitPrice,
      netPnl: t.netPnl,
      size: t.size,
      timeframe: t.timeframe,
      trader: DEFAULT_TRADER,
    });
    return true;
  }

  /** Replace all entries owned by `trader` with `entries` (idempotent import of
   * another trader's book — re-importing updates rather than duplicating). */
  async importTraderTrades(trader: string, entries: TradeEntry[]): Promise<void> {
    this.entries = [...entries, ...this.entries.filter((e) => (e.trader ?? DEFAULT_TRADER) !== trader)];
    await this.persistEntries();
    if (trader !== DEFAULT_TRADER && !this.customTraders.includes(trader)) {
      this.customTraders.push(trader);
      await storageSet(TRADERS_KEY, this.customTraders);
    }
    this.changed.emit();
  }

  async deleteTrade(id: string): Promise<void> {
    const entry = this.entries.find((e) => e.id === id);
    if (entry) await this.deleteImageIfPresent(entry);
    this.entries = this.entries.filter((e) => e.id !== id);
    await this.persistEntries();
    this.changed.emit();
  }

  async addCustomTag(tag: string): Promise<void> {
    const t = tag.trim();
    if (!t || this.customTags.includes(t) || defaultTags.includes(t)) return;
    this.customTags.push(t);
    await storageSet(TAGS_KEY, this.customTags);
    this.changed.emit();
  }

  get allTags(): string[] {
    return [...defaultTags, ...this.customTags];
  }

  /** All trader names: "Me" first, then added + any present in entries. */
  get traders(): string[] {
    const set = new Set<string>(this.customTraders);
    for (const e of this.entries) set.add(e.trader ?? DEFAULT_TRADER);
    set.delete(DEFAULT_TRADER);
    return [DEFAULT_TRADER, ...[...set].sort()];
  }

  async addTrader(name: string): Promise<void> {
    const t = name.trim();
    if (!t || t === DEFAULT_TRADER || this.customTraders.includes(t)) return;
    this.customTraders.push(t);
    await storageSet(TRADERS_KEY, this.customTraders);
    this.changed.emit();
  }

  /** Remove a trader: drops the name and deletes all of their entries. */
  async removeTrader(name: string): Promise<void> {
    if (name === DEFAULT_TRADER) return;
    this.customTraders = this.customTraders.filter((t) => t !== name);
    const toDelete = this.entries.filter((e) => (e.trader ?? DEFAULT_TRADER) === name);
    for (const e of toDelete) if (e.hasImage) await storageRemove(imageKey(e.id));
    this.entries = this.entries.filter((e) => (e.trader ?? DEFAULT_TRADER) !== name);
    await storageSet(TRADERS_KEY, this.customTraders);
    await this.persistEntries();
    this.changed.emit();
  }

  /** Entries from the current calendar week (local time, week starts Monday). */
  get thisWeekEntries(): TradeEntry[] {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const daysSinceMonday = (startOfDay.getDay() + 6) % 7;
    const weekStart = startOfDay.getTime() - daysSinceMonday * 86400_000;
    const weekEnd = weekStart + 7 * 86400_000;
    return this.entries.filter((e) => e.date >= weekStart && e.date < weekEnd);
  }

  get winRate(): number | null {
    const week = this.thisWeekEntries;
    if (!week.length) return null;
    return (week.filter((e) => e.rMultiple > 0).length / week.length) * 100;
  }

  get avgR(): number | null {
    const week = this.thisWeekEntries;
    if (!week.length) return null;
    return week.reduce((sum, e) => sum + e.rMultiple, 0) / week.length;
  }

  get profitFactor(): number | null {
    const week = this.thisWeekEntries;
    const gains = week.filter((e) => e.rMultiple > 0).reduce((s, e) => s + e.rMultiple, 0);
    const losses = Math.abs(week.filter((e) => e.rMultiple < 0).reduce((s, e) => s + e.rMultiple, 0));
    if (losses <= 0) return null;
    return gains / losses;
  }

  /** A flat, mappable CSV — one row per trade. Columns are named so other
   * journals (Tradezella/TraderSync/etc., which map columns on import) can
   * ingest it: Date, Symbol, Side, Qty, Entry, Exit, R, NetPnL, Setup,
   * Strategy, Mode, plus the free-text reflection. */
  csvString(): string {
    const cols = ["Date", "Symbol", "Side", "Qty", "Entry", "Exit", "R", "NetPnL", "Setup", "Strategy", "Mode", "Thesis", "What Happened", "Lesson"];
    const esc = (s: string) => `"${String(s).replaceAll('"', '""')}"`;
    let csv = cols.join(",") + "\n";
    for (const e of this.entries) {
      csv += [
        new Date(e.date).toISOString(),
        esc(e.symbol),
        e.direction === "short" ? "Sell" : "Buy",
        e.size ?? "",
        e.entryPrice ?? "",
        e.exitPrice ?? "",
        e.rMultiple,
        e.netPnl ?? "",
        esc(e.setupTag),
        esc(e.strategy ?? ""),
        e.mode ?? "manual",
        esc(e.thesis),
        esc(e.whatHappened),
        esc(e.lesson),
      ].join(",") + "\n";
    }
    return csv;
  }

  /** Parse a flat trades CSV (our own export, or a broker/platform export with
   * the common columns) into entries under `trader`. Header-mapped and
   * tolerant: Symbol + a date are the only hard requirements. */
  parseCsv(text: string): TradeEntry[] {
    const rows = csvRows(text);
    if (rows.length < 2) return [];
    const header = rows[0]!.map((h) => h.trim().toLowerCase());
    const col = (...names: string[]) => { for (const n of names) { const i = header.indexOf(n); if (i >= 0) return i; } return -1; };
    const ci = {
      date: col("date", "date/time", "time", "opened", "open time"),
      symbol: col("symbol", "ticker", "instrument"),
      side: col("side", "direction", "b/s", "type"),
      qty: col("qty", "quantity", "size", "contracts", "shares"),
      entry: col("entry", "entry price", "avg entry", "price"),
      exit: col("exit", "exit price", "avg exit"),
      r: col("r", "r multiple", "rmultiple"),
      pnl: col("netpnl", "net pnl", "pnl", "p/l", "profit", "realized p/l"),
      setup: col("setup", "tag", "strategy tag"),
      strategy: col("strategy"),
    };
    const num = (v: string | undefined) => { const n = Number((v ?? "").replace(/[^0-9.\-]/g, "")); return Number.isFinite(n) && v?.trim() ? n : undefined; };
    const out: TradeEntry[] = [];
    for (const r of rows.slice(1)) {
      const sym = (ci.symbol >= 0 ? r[ci.symbol] : "")?.trim().toUpperCase();
      const rawDate = ci.date >= 0 ? r[ci.date] : "";
      const ms = rawDate ? (Number.isFinite(Number(rawDate)) ? Number(rawDate) : Date.parse(rawDate)) : NaN;
      if (!sym || !Number.isFinite(ms)) continue;
      const side = (ci.side >= 0 ? r[ci.side] : "")?.toLowerCase() ?? "";
      const dir: TradeDirection = /sell|short|s\b/.test(side) ? "short" : "long";
      out.push({
        id: crypto.randomUUID(),
        date: ms,
        symbol: sym,
        direction: dir,
        rMultiple: num(r[ci.r]) ?? 0,
        setupTag: (ci.setup >= 0 ? r[ci.setup] : "")?.trim() || "imported",
        thesis: "", whatHappened: "", lesson: "",
        size: num(r[ci.qty]),
        entryPrice: num(r[ci.entry]),
        exitPrice: num(r[ci.exit]),
        netPnl: num(r[ci.pnl]),
        strategy: (ci.strategy >= 0 ? r[ci.strategy]?.trim() : "") || undefined,
      });
    }
    return out;
  }
}

export const journalStore = new JournalStore();
